import { useTranslation } from 'react-i18next';

import { LockClosedIcon } from '@heroicons/react/24/solid';
import { Link, useLocation } from '@remix-run/react';
import cx from 'classnames';

import { SubscriberGate } from '~/components/Actions/types';
import { serializeNode } from '~/components/DreamBuilder/nodes/serializeNode';
import Text from '~/components/Text';
import type { Publication } from '~/interfaces/publication';

import { getSignupNodeContent } from './utils/getSignupNodeContent';

interface GatedContentProps {
  title: string | null;
  message: string | null;
  publication: Publication;
  cta?: string | null;
  createActionType?: SubscriberGate;
  theme?: 'subscribe' | 'subscribe-gate' | 'subscribe-gate-bottom' | 'content' | 'navs' | 'default' | 'primary';
}

const FreeGatedContent = ({ cta, createActionType, title, message, publication, theme }: GatedContentProps) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const backgroundColor = publication.web_theme.background_color;
  const bodyColor = publication.web_theme.primary_color;

  return (
    <div
      className={cx(
        'mx-auto flex w-full flex-col items-center px-10',
        {
          'mb-10 bg-wt-text-on-background py-10 text-wt-background': theme === 'subscribe-gate',
        },
        {
          'max-w-xl bg-transparent': theme === 'subscribe-gate-bottom',
        },
        { 'max-w-2xl': theme !== 'subscribe-gate-bottom' },
      )}
    >
      {publication.private && <LockClosedIcon className="w-16 h-16" />}

      <div className="flex flex-col space-y-2">
        <Text
          as="h2"
          type="title"
          size={theme === 'subscribe-gate-bottom' ? '2xl' : '3xl'}
          theme={theme}
          className="text-3xl font-bold leading-tight tracking-normal text-center"
        >
          {title}
        </Text>
        <Text
          as="p"
          type="body"
          size={theme === 'subscribe-gate-bottom' ? 'md' : 'lg'}
          theme={theme}
          className="leading-tight tracking-normal text-center"
        >
          {message}
        </Text>
      </div>
      {!(publication.private && !publication.require_subscriber_approval) && (
        <div className="w-full py-8">
          {serializeNode(
            getSignupNodeContent(publication.web_theme, {
              redirectUrl: createActionType === SubscriberGate.GATED ? pathname : undefined, // backwards compatible support for SubscriberGate.GATED type Signup element
              buttonText: cta || t('shared.cta.subscribe'),
              buttonSubmittingText: `${t('shared.actions.submitting')}...`,
              gdprTextColor: theme === 'subscribe-gate' ? backgroundColor : bodyColor,
              inputFontSize: theme === 'subscribe-gate-bottom' ? '14px' : undefined, // text-sm
              buttonFontSize: theme === 'subscribe-gate-bottom' ? '14px' : undefined, // text-sm
              inputPlaceholder: t('subscribe.email_placeholder'),
            }),
          )}
        </div>
      )}
      <div className="pb-2">
        <Text as="span" type="body" theme={theme} className="flex items-center justify-center text-sm text-center">
          {t('login.already_a_subscriber')}
          <Link to="/?modal=login" className="ml-1">
            <span
              className={cx('underline underline-offset-1', {
                'text-wt-text-on-background': theme !== 'subscribe-gate',
              })}
            >
              {t('shared.cta.sign_in')}
            </span>
            .
          </Link>
        </Text>
        <Link to="/" className="cursor-pointer">
          <Text as="span" type="body" theme={theme} className="flex justify-center py-2 text-sm text-center sm:text-lg">
            <span className="text-sm underline underline-offset-1">{t('login.not_now')}</span>
          </Text>
        </Link>
      </div>
    </div>
  );
};

export default FreeGatedContent;
